/**
 * Genera entries de proyección de impuestos (IVA neto + PPM) por período tributario,
 * para mostrar en el panel futuro de FlujoCajaV2 y el dashboard de IVA.
 *
 * Reglas:
 * - IVA débito: suma monto_iva de emitidas. Notas de crédito (DTE 61) restan.
 * - IVA crédito: suma monto_iva de recibidas. Notas de crédito (DTE 61) restan.
 * - IVA neto = débito - crédito (solo se proyecta si > 0).
 * - PPM 2% se calcula sobre ventas afectas (DTE 33 + 56). Excluye exenta (34),
 *   nota de crédito (61), exportación (110). Régimen Pro Pyme tiene tasas
 *   distintas — eso se valida con contador y se pasa por parámetro a futuro.
 * - Fecha de pago: día 12 del mes siguiente al período.
 * - Conciliación real contra movimientos_caja (detectarEstadoPago): un período 'pagado'
 *   (F29 saldado en caja) NO se proyecta; 'vencido' impago se proyecta y se marca en rojo;
 *   'por_pagar' (futuro) se proyecta normal. Corte: se ignoran períodos < 2026-01 (saldados
 *   por saldo inicial) y registros malformados que no matcheen /^\d{4}-\d{2}$/.
 *
 * Adicionalmente, proyecta los meses sin datos SII hasta diciembre del año
 * siguiente, basándose en el promedio de los últimos 3 períodos con datos
 * (estimación). Esos entries llevan `estimado: true` y se reemplazan por el
 * valor real cuando llega el dato SII.
 *
 * @param {Array} emitidas - facturas_sii_emitidas con monto_iva, monto_neto, periodo_tributario, tipo_dte
 * @param {Array} recibidas - facturas_sii_recibidas con monto_iva, periodo_tributario, tipo_dte
 * @param {string} pais - "Chile" | "Peru" (afecta solo el label IVA/IGV)
 * @returns {Array} entries con shape { id, fecha_display, importeEnMoneda, razon_social_receptor, tipo, estimado? }
 */
import { detectarEstadoPago } from "./iva";

export const calcularImpuestos = (
  emitidas,
  recibidas,
  pais,
  movimientosCaja = [],
  // Fase B2: fuentes reales para afinar componentes (ver ARQUITECTURA / f29-sync, honorarios-sync).
  // f29Declaraciones: filas de f29_declaraciones (períodos Pagado con desglose por código).
  // honorariosRetencion: filas de honorarios_retencion (retención real por período).
  // La LÓGICA que las consume se agrega en la etapa 2; en la etapa 1 solo se cablea la firma.
  f29Declaraciones = [],
  honorariosRetencion = [],
  // Fase B3: RCV (Registro de Compras y Ventas aceptado por el SII) por período → IVA determinado
  // real (débito − crédito aceptado). Reemplaza la resta ingenua para meses cerrados con dato.
  rcvPeriodo = [],
  // Override manual del contador (impuestos_f29_override): {periodo, ppm, iut}. Si ppm/iut no es
  // null → se usa ese valor SIN tag "Estimado" (solo aplica a meses cerrados, rama real).
  f29Override = []
) => {
  const entries = [];
  const tasaPPM = 0.02;
  const TIPOS_AFECTOS_PPM = [33, 56];

  // Mes en curso (YYYY-MM). "Cerrado" = periodo < mesActual. Solo los meses cerrados usan dato
  // real (RCV / honorarios / F29); el mes en curso y los futuros van estimados, una sola vez.
  // Esto evita el doble conteo del mes en curso, que tiene dato REAL parcial (incompleto) además
  // de su fila estimada.
  const mesActual = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();

  const ivaVentasPorMes = {};
  const ivaComprasPorMes = {};
  const ventasNetasPorMes = {};

  for (const f of emitidas) {
    const mes = f.periodo_tributario;
    if (!mes) continue;
    const signoEmitida = f.tipo_dte === 61 ? -1 : 1;
    ivaVentasPorMes[mes] = (ivaVentasPorMes[mes] || 0) + signoEmitida * Number(f.monto_iva || 0);
    ventasNetasPorMes[mes] =
      (ventasNetasPorMes[mes] || 0) + (TIPOS_AFECTOS_PPM.includes(f.tipo_dte) ? Number(f.monto_neto || 0) : 0);
  }

  for (const f of recibidas) {
    const mes = f.periodo_tributario;
    if (!mes) continue;
    const signoRecibida = f.tipo_dte === 61 ? -1 : 1;
    ivaComprasPorMes[mes] = (ivaComprasPorMes[mes] || 0) + signoRecibida * Number(f.monto_iva || 0);
  }

  // ── Fase B2: afinado de componentes del F29 con fuentes reales (Chile) ──
  // Tasa real de PPM e IUT estimado desde el F29 pagado más reciente. NO se usa el código 115
  // (viene ambiguo = "2"): la tasa se DERIVA ppm_neto / ventasNetas(ese período) con la MISMA
  // fórmula de ventasNetasPorMes. Fallback a 2% / sin IUT si no hay F29 pagado.
  const f29Pagados = (f29Declaraciones || [])
    .filter((f) => f.status === "Pagado" && f.ppm_neto != null)
    .sort((a, b) => (a.periodo < b.periodo ? 1 : -1)); // más reciente primero
  const f29Reciente = f29Pagados[0] || null;
  const tasaPPMReal =
    f29Reciente && ventasNetasPorMes[f29Reciente.periodo] > 0
      ? f29Reciente.ppm_neto / ventasNetasPorMes[f29Reciente.periodo]
      : tasaPPM;
  // IUT: código 48 del F29 pagado más reciente. Mismo valor para impago y futuros → SIEMPRE estimado.
  const iutEstimado = f29Reciente && f29Reciente.iut != null ? Number(f29Reciente.iut) : 0;

  // Retención de honorarios real por período + promedio (últimos 3 con dato) para meses futuros.
  const honorariosPorPeriodo = {};
  for (const h of honorariosRetencion || []) {
    honorariosPorPeriodo[h.periodo] = Number(h.retencion_total || 0);
  }
  const ultimos3Hon = Object.keys(honorariosPorPeriodo).sort().slice(-3);
  const promedioHonorarios =
    ultimos3Hon.length > 0
      ? Math.round(ultimos3Hon.reduce((s, p) => s + honorariosPorPeriodo[p], 0) / ultimos3Hon.length)
      : 0;

  // Fase B3: IVA determinado REAL por período desde el RCV (débito − crédito ACEPTADO por el SII).
  // Reemplaza la resta ingenua ivaVentas − ivaCompras (que sumaba recibidas pendientes/rechazadas).
  const ivaDeterminadoRcvPorPeriodo = {};
  for (const r of rcvPeriodo || []) {
    if (r.iva_determinado != null) ivaDeterminadoRcvPorPeriodo[r.periodo] = Number(r.iva_determinado);
  }

  // Override manual del contador por período (PPM/IUT). null = usar estimación.
  const overridePorPeriodo = {};
  for (const o of f29Override || []) {
    overridePorPeriodo[o.periodo] = {
      ppm: o.ppm != null ? Number(o.ppm) : null,
      iut: o.iut != null ? Number(o.iut) : null,
    };
  }

  const periodos = [...new Set([...Object.keys(ivaVentasPorMes), ...Object.keys(ivaComprasPorMes)])].sort();

  for (const periodo of periodos) {
    // Corte: ignorar períodos pre-2026 (saldados por saldo inicial) y registros malformados
    // (ej. "202512", que además rompería el parseo de mm).
    if (!/^\d{4}-\d{2}$/.test(periodo) || periodo < "2026-01") continue;

    // Solo meses CERRADOS usan la rama real. El mes en curso y los futuros (aunque ya tengan
    // dato SII/RCV parcial) caen a la rama de estimados de abajo → se generan una sola vez.
    if (periodo >= mesActual) continue;

    const [yyyy, mm] = periodo.split("-");
    const mesSiguiente = parseInt(mm, 10) === 12 ? 1 : parseInt(mm, 10) + 1;
    const añoSiguiente = parseInt(mm, 10) === 12 ? parseInt(yyyy, 10) + 1 : parseInt(yyyy, 10);
    const fechaPago = `${añoSiguiente}-${String(mesSiguiente).padStart(2, "0")}-12`;

    // Conciliación real contra caja (reemplaza el viejo guard `if (fechaPago <= hoy) continue`):
    // 'pagado' → no proyectar (F29 saldado); 'vencido' → proyectar en rojo; 'por_pagar' → normal.
    // IVA y PPM del período comparten estado: un único pago F29 salda ambos.
    const estado = detectarEstadoPago(periodo, fechaPago, movimientosCaja).estado;
    if (estado === "pagado") continue;
    const vencida = estado === "vencido";

    const ivaVentas = ivaVentasPorMes[periodo] || 0;
    const ivaCompras = ivaComprasPorMes[periodo] || 0;
    // IVA determinado: RCV real si hay dato del período (mes cerrado); si no, resta ingenua
    // (fallback). Si el RCV da ≤ 0, el guard `ivaNeto > 0` de abajo omite la obligación de IVA.
    const ivaNeto =
      ivaDeterminadoRcvPorPeriodo[periodo] != null ? ivaDeterminadoRcvPorPeriodo[periodo] : ivaVentas - ivaCompras;
    const ventasNetas = ventasNetasPorMes[periodo] || 0;
    // PPM: override del contador si está cargado (sin tag); si no, estimación con tasa real (con tag).
    const ppmOverride = overridePorPeriodo[periodo]?.ppm;
    const ppm = ppmOverride != null ? ppmOverride : Math.round(ventasNetas * tasaPPMReal);
    const ppmEstimado = ppmOverride == null;
    // IUT: override del contador si está cargado (sin tag); si no, estimación (con tag).
    const iutOverride = overridePorPeriodo[periodo]?.iut;
    const iutValor = iutOverride != null ? iutOverride : iutEstimado;
    const iutEsEstimado = iutOverride == null;

    const labelIVA = pais === "Chile" ? "IVA" : "IGV";

    if (ivaNeto > 0) {
      entries.push({
        id: `iva-${periodo}`,
        fecha_display: fechaPago,
        importeEnMoneda: -ivaNeto,
        razon_social_receptor: `${labelIVA} ${periodo}`,
        tipo: "impuesto",
        vencida,
      });
    }

    if (ppm > 0) {
      entries.push({
        id: `ppm-${periodo}`,
        fecha_display: fechaPago,
        importeEnMoneda: -ppm,
        razon_social_receptor: `PPM ${periodo}`,
        tipo: "impuesto",
        vencida,
        estimado: ppmEstimado,
      });
    }

    // Retención de honorarios: real del período (mes cerrado → SIN tag). Si faltara la fila
    // (no debería para un mes cerrado), cae al promedio y se marca estimado.
    const honorarioReal = honorariosPorPeriodo[periodo];
    const retencionHon = honorarioReal != null ? honorarioReal : promedioHonorarios;
    if (retencionHon > 0) {
      entries.push({
        id: `honorarios-${periodo}`,
        fecha_display: fechaPago,
        importeEnMoneda: -retencionHon,
        razon_social_receptor: `Retención Honorarios ${periodo}`,
        tipo: "impuesto",
        vencida,
        estimado: honorarioReal == null,
      });
    }

    // IUT: override del contador (sin tag) o estimación (código 48 del último F29 pagado, con tag).
    if (iutValor > 0) {
      entries.push({
        id: `iut-${periodo}`,
        fecha_display: fechaPago,
        importeEnMoneda: -iutValor,
        razon_social_receptor: `IUT ${periodo}`,
        tipo: "impuesto",
        vencida,
        estimado: iutEsEstimado,
      });
    }
  }

  const labelIVAEst = pais === "Chile" ? "IVA" : "IGV";
  const periodosConDatos = periodos.filter((p) => ivaVentasPorMes[p] > 0);
  const ultimos3 = periodosConDatos.slice(-3);

  if (ultimos3.length > 0) {
    const promedioIVA = Math.round(
      ultimos3.reduce((sum, p) => {
        const ivaNeto = (ivaVentasPorMes[p] || 0) - (ivaComprasPorMes[p] || 0);
        return sum + Math.max(ivaNeto, 0);
      }, 0) / ultimos3.length
    );

    const promedioPPM = Math.round(
      ultimos3.reduce((sum, p) => sum + Math.round((ventasNetasPorMes[p] || 0) * tasaPPMReal), 0) / ultimos3.length
    );

    const hoyDate = new Date();
    // Replicar el promedio mes a mes desde el mes actual hasta diciembre del año
    // siguiente, para alimentar la vista completa (sin corte temporal) del panel futuro.
    const mesesHastaFinSiguiente =
      (hoyDate.getFullYear() + 1) * 12 + 11 - (hoyDate.getFullYear() * 12 + hoyDate.getMonth());
    for (let i = 0; i <= mesesHastaFinSiguiente; i++) {
      const mesProyectado = new Date(hoyDate.getFullYear(), hoyDate.getMonth() + i, 1);
      const yyyy = mesProyectado.getFullYear();
      const mm = String(mesProyectado.getMonth() + 1).padStart(2, "0");
      const periodoProyectado = `${yyyy}-${mm}`;

      // Mismo corte que la rama real (defensivo — los estimados arrancan en el mes actual,
      // siempre >= 2026-01 y bien formados).
      if (!/^\d{4}-\d{2}$/.test(periodoProyectado) || periodoProyectado < "2026-01") continue;

      // Sin skip por dato SII: la rama real ya se limitó a meses CERRADOS (< mesActual) y esta
      // arranca en el mes actual, así que no hay solape de períodos. El mes en curso se estima
      // aquí aunque tenga dato parcial (evita dejarlo sin fila y evita duplicarlo).

      const mesPago = mesProyectado.getMonth() + 2 > 12 ? 1 : mesProyectado.getMonth() + 2;
      const añoPago = mesProyectado.getMonth() + 2 > 12 ? yyyy + 1 : yyyy;
      const fechaPago = `${añoPago}-${String(mesPago).padStart(2, "0")}-12`;

      if (promedioIVA > 0) {
        entries.push({
          id: `iva-est-${periodoProyectado}`,
          fecha_display: fechaPago,
          importeEnMoneda: -promedioIVA,
          razon_social_receptor: `${labelIVAEst} ${periodoProyectado}`,
          tipo: "impuesto",
          estimado: true,
        });
      }

      if (promedioPPM > 0) {
        entries.push({
          id: `ppm-est-${periodoProyectado}`,
          fecha_display: fechaPago,
          importeEnMoneda: -promedioPPM,
          razon_social_receptor: `PPM ${periodoProyectado}`,
          tipo: "impuesto",
          estimado: true,
        });
      }

      // Retención de honorarios estimada (promedio) — mes futuro sin dato real. Con tag.
      if (promedioHonorarios > 0) {
        entries.push({
          id: `honorarios-est-${periodoProyectado}`,
          fecha_display: fechaPago,
          importeEnMoneda: -promedioHonorarios,
          razon_social_receptor: `Retención Honorarios ${periodoProyectado}`,
          tipo: "impuesto",
          estimado: true,
        });
      }

      // IUT estimado (código 48 del último F29 pagado). Con tag.
      if (iutEstimado > 0) {
        entries.push({
          id: `iut-est-${periodoProyectado}`,
          fecha_display: fechaPago,
          importeEnMoneda: -iutEstimado,
          razon_social_receptor: `IUT ${periodoProyectado}`,
          tipo: "impuesto",
          estimado: true,
        });
      }
    }
  }

  return entries;
};
